import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, useAuthStore } from '@dukene/shared';

export enum SupplierStatus {
    PENDING = 'PENDING',
    APPROVED = 'APPROVED',
    SUSPENDED = 'SUSPENDED',
    REJECTED = 'REJECTED',
    INACTIVE = 'INACTIVE',
}

export interface Supplier {
    id: string;
    name: string;
    phoneNumber: string;
    email: string | null;
    company: string | null;
    address: string | null;
    city: string | null;
    country: string | null;
    postalCode: string | null;
    tags: string[];
    isMarketplace: boolean;
    requiresOAuth: boolean;
    integrationType: 'CATALOG' | 'API_OAUTH' | 'API_KEY';
    createdAt: string;
    updatedAt: string;
    lastLoginAt: string | null;
}

export interface StoreSupplier {
    id: string;
    storeId: string;
    supplierId: string;
    supplier: Supplier;
    status: SupplierStatus;
    isActive: boolean;
    canViewOrders: boolean;
    canFulfillOrders: boolean;
    canViewInventory: boolean;
    canViewProducts: boolean;
    notes: string | null;
    totalOrders: number;
    completedOrders: number;
    averageRating: number | null;
    connectionType: 'MANUAL' | 'MARKETPLACE' | 'API_INTEGRATED';
    autoSyncProducts: boolean;
    autoSyncInventory: boolean;
    autoSyncPricing: boolean;
    lastSyncAt: string | null;
    createdAt: string;
    updatedAt: string;
    _count?: {
        orders: number;
        fulfillments: number;
    };
    orders?: any[];
}

export interface SuppliersResponse {
    suppliers: StoreSupplier[];
    pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
    };
}

export interface CreateSupplierDto {
    storeId: string;
    name: string;
    phoneNumber: string;
    email?: string;
    company?: string;
    address?: string;
    city?: string;
    country?: string;
    postalCode?: string;
    canViewOrders?: boolean;
    canFulfillOrders?: boolean;
    canViewInventory?: boolean;
    canViewProducts?: boolean;
    notes?: string;
    tags?: string[];
}

export interface UpdateSupplierDto {
    name?: string;
    phoneNumber?: string;
    email?: string;
    company?: string;
    address?: string;
    city?: string;
    country?: string;
    postalCode?: string;
    status?: SupplierStatus;
    isActive?: boolean;
    canViewOrders?: boolean;
    canFulfillOrders?: boolean;
    canViewInventory?: boolean;
    canViewProducts?: boolean;
    notes?: string;
    tags?: string[];
}

export interface SupplierFilters {
    status?: string;
    isActive?: string;
    search?: string;
    page?: number;
    limit?: number;
}

export interface SupplierStats {
    supplier: {
        id: string;
        name: string;
        status: SupplierStatus;
        totalOrders: number;
        completedOrders: number;
        averageRating: number | null;
    };
    stats: {
        ordersByStatus: Array<{ status: string; _count: number }>;
        fulfillmentsByStatus: Array<{ status: string; _count: number }>;
        completionRate: number;
        totalFulfillments: number;
    };
}

/**
 * Fetch all suppliers for a store
 */
export const useSuppliers = (storeId: string, filters?: SupplierFilters) => {
    const queryParams = new URLSearchParams();

    if (filters?.status) queryParams.append('status', filters.status);
    if (filters?.isActive !== undefined) queryParams.append('isActive', filters.isActive);
    if (filters?.search) queryParams.append('search', filters.search);
    if (filters?.page) queryParams.append('page', filters.page.toString());
    if (filters?.limit) queryParams.append('limit', filters.limit.toString());

    return useQuery<SuppliersResponse>({
        queryKey: ['suppliers', storeId, filters],
        queryFn: async () => {
            return apiClient.get<SuppliersResponse>(
                `/suppliers/store/${storeId}?${queryParams.toString()}`
            );
        },
        enabled: !!storeId,
    });
};

/**
 * Fetch single supplier by ID
 */
export const useSupplier = (supplierId: string | null) => {
    const activeStoreId = useAuthStore((state) => state.activeStoreId);

    return useQuery<StoreSupplier>({
        queryKey: ['supplier', activeStoreId, supplierId],
        queryFn: async () => {
            if (!supplierId) throw new Error('Supplier ID is required');
            return apiClient.get<StoreSupplier>(`/suppliers/${supplierId}`);
        },
        enabled: !!supplierId && !!activeStoreId,
    });
};

/**
 * Create new supplier
 */
export const useCreateSupplier = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateSupplierDto) => {
            return apiClient.post<StoreSupplier>('/suppliers', data);
        },
        onSuccess: () => {
            const storeId = useAuthStore.getState().activeStoreId;
            queryClient.invalidateQueries({ queryKey: ['suppliers', storeId] });
        },
    });
};

/**
 * Update existing supplier
 */
export const useUpdateSupplier = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: string; data: UpdateSupplierDto }) => {
            return apiClient.put<StoreSupplier>(`/suppliers/${id}`, data);
        },
        onSuccess: (_, variables) => {
            const storeId = useAuthStore.getState().activeStoreId;
            queryClient.invalidateQueries({ queryKey: ['suppliers', storeId] });
            queryClient.invalidateQueries({ queryKey: ['supplier', storeId, variables.id] });
        },
    });
};

/**
 * Delete supplier
 */
export const useDeleteSupplier = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (supplierId: string) => {
            return apiClient.delete(`/suppliers/${supplierId}`);
        },
        onSuccess: () => {
            const storeId = useAuthStore.getState().activeStoreId;
            queryClient.invalidateQueries({ queryKey: ['suppliers', storeId] });
        },
    });
};

/**
 * Assign order to supplier
 */
export const useAssignOrder = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ supplierId, orderId }: { supplierId: string; orderId: string }) => {
            return apiClient.post(`/suppliers/${supplierId}/assign-order`, { orderId });
        },
        onSuccess: (_, variables) => {
            const storeId = useAuthStore.getState().activeStoreId;
            queryClient.invalidateQueries({ queryKey: ['supplier', storeId, variables.supplierId] });
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        },
    });
};

/**
 * Get supplier statistics
 */
export const useSupplierStats = (supplierId: string | null) => {
    const activeStoreId = useAuthStore((state) => state.activeStoreId);

    return useQuery<SupplierStats>({
        queryKey: ['supplier-stats', activeStoreId, supplierId],
        queryFn: async () => {
            if (!supplierId) throw new Error('Supplier ID is required');
            return apiClient.get<SupplierStats>(`/suppliers/${supplierId}/stats`);
        },
        enabled: !!supplierId && !!activeStoreId,
    });
};
